import { useState } from "react";
import { supabase } from "../supabaseClient";
import { splitText, parseUserRows, rowProblem } from "../lib/bulkUsers";

// Lägg till flera lärare med samma roll: klistra in från Excel eller ladda upp
// filen, kontrollera listan (komplettera LU-id där adressen inte är en
// LU-adress) och skicka. Servern (api/bulk-invite.js) lägger till rollen för
// befintliga konton och sparar en väntande behörighet för nya, och mejlar alla.

const STATUS_TEXT = {
  added: "Rollen tillagd",
  already: "Hade redan rollen",
  pending: "Inbjuden",
  pending_again: "Inbjuden (fanns redan i kö)",
  error: "Fel",
};

function BulkAddUsers({ G, css, assignableRoles, renderScope, scopeFor, scopeComplete, onClose, onDone }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);         // tolkad lista att kontrollera
  const [roleData, setRoleData] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const FL = ({ children }) => <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5, marginTop: 14 }}>{children}</label>;
  const btn = (primary) => ({
    background: primary ? G.gold : "none", border: primary ? "none" : `1px solid ${G.border}`, borderRadius: 6,
    color: primary ? G.bg : G.muted, fontSize: 14, fontWeight: primary ? 600 : 400, padding: "9px 18px",
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
  });

  function applyTable(table) {
    const { rows: parsed } = parseUserRows(table);
    if (parsed.length === 0) { setError("Hittade inga personer. Varje rad behöver minst en e-postadress eller ett LU-id."); return; }
    setError(null);
    setRows(parsed);
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      applyTable(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" }));
    } catch {
      setError("Filen kunde inte läsas. Spara den som .xlsx eller klistra in raderna i stället.");
    }
    e.target.value = "";
  }

  const problems = (rows || []).map(rowProblem);
  const blocking = problems.filter(p => p === "invalid_email" || p === "missing").length;
  const needsLuId = problems.filter(p => p === "needs_lu_id").length;
  const roleReady = roleData.role && scopeComplete(roleData.role, roleData);

  async function submit() {
    setBusy(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/bulk-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ rows, role: roleData.role, ...scopeFor(roleData.role, roleData) }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.error === "not_configured"
          ? "Utskicket är inte uppsatt (miljövariablerna i Vercel saknas)."
          : json.error || `Något gick fel (${res.status}).`);
      } else {
        setResults(json.results || []);
        onDone?.();
      }
    } catch {
      setError("Ingen kontakt med servern. Försök igen.");
    }
    setBusy(false);
  }

  const cell = { padding: "7px 8px", borderBottom: `1px solid ${G.border}`, fontSize: 13, color: G.text, verticalAlign: "middle" };
  const head = { ...cell, color: G.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left" };

  // ── Resultat ──
  if (results) {
    const count = s => results.filter(r => r.status === s).length;
    return (
      <>
        <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 6px" }}>Klart</h3>
        <p style={{ fontSize: 14, color: G.sub, margin: "0 0 14px", lineHeight: 1.5 }}>
          {count("pending") + count("pending_again")} inbjudna, {count("added")} fick rollen på sitt befintliga konto
          {count("already") ? `, ${count("already")} hade den redan` : ""}
          {count("error") ? `, ${count("error")} med fel` : ""}.
          {" "}De inbjudna får behörigheten när de loggar in med LU första gången.
        </p>
        <div style={{ overflowX: "auto", border: `1px solid ${G.border}`, borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={head}>Namn</th><th style={head}>E-post / LU-id</th><th style={head}>Resultat</th></tr></thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i}>
                  <td style={cell}>{r.name || "–"}</td>
                  <td style={{ ...cell, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{r.email || r.lu_id}</td>
                  <td style={{ ...cell, color: r.status === "error" ? G.red : G.text }}>
                    {STATUS_TEXT[r.status] || r.status}
                    {r.status !== "error" && r.status !== "already" && r.mailed === false ? " · inget mejl" : ""}
                    {r.message ? ` · ${r.message}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btn(true)}>Stäng</button>
        </div>
      </>
    );
  }

  // ── Steg 1: klistra in eller ladda upp ──
  if (!rows) {
    return (
      <>
        <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 4px" }}>Lägg till flera</h3>
        <p style={{ fontSize: 13, color: G.muted, margin: "0 0 14px", lineHeight: 1.5 }}>
          Ladda upp en Excel-fil eller klistra in raderna. Kolumner: Namn, E-post och vid behov LU-id. Alla får samma roll.
        </p>
        <FL>Excel-fil</FL>
        <input id="bulk-file" type="file" accept=".xlsx,.xls,.csv" onChange={onFile} style={{ fontSize: 13, color: G.text }} />
        <FL>Eller klistra in</FL>
        <textarea
          id="bulk-text"
          style={{ ...css.textarea, minHeight: 140, fontFamily: "'DM Mono', monospace", fontSize: 13 }}
          placeholder={"Namn\tEpost\tLU-id\nAnna Andersson\tanna.andersson@med.lu.se\nIda Persson\tida.a.persson@skane.se\tid1234pe"}
          value={text}
          onChange={e => setText(e.target.value)}
        />
        {error && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: G.red }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => applyTable(splitText(text))} disabled={!text.trim()} style={{ ...btn(true), opacity: text.trim() ? 1 : 0.5 }}>Fortsätt</button>
          <button onClick={onClose} style={btn(false)}>Avbryt</button>
        </div>
      </>
    );
  }

  // ── Steg 2: kontrollera listan, välj roll ──
  return (
    <>
      <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 4px" }}>
        Lägg till {rows.length} {rows.length === 1 ? "person" : "personer"}
      </h3>
      <p style={{ fontSize: 13, color: G.muted, margin: "0 0 12px", lineHeight: 1.5 }}>
        Den som inte har en LU-adress behöver sitt LU-id, annars hittar appen inte kontot när hen loggar in.
      </p>
      {needsLuId > 0 && (
        <div style={{ background: G.gold + "14", border: `1px solid ${G.gold}55`, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: G.text, marginBottom: 12 }}>
          {needsLuId} {needsLuId === 1 ? "person saknar" : "personer saknar"} LU-id. Fyll i det i tabellen, eller skicka ändå — då kopplas behörigheten bara om LU-kontot har samma e-postadress.
        </div>
      )}
      <div style={{ overflowX: "auto", border: `1px solid ${G.border}`, borderRadius: 8, maxHeight: 320, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={head}>Namn</th><th style={head}>E-post</th><th style={head}>LU-id</th></tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const p = problems[i];
              return (
                <tr key={i}>
                  <td style={cell}>{r.name || "–"}</td>
                  <td style={{ ...cell, fontFamily: "'DM Mono', monospace", fontSize: 12, color: p === "invalid_email" ? G.red : G.text }}>
                    {r.email || "–"}
                    {p === "invalid_email" && <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>Ogiltig adress</div>}
                  </td>
                  <td style={cell}>
                    <input
                      aria-label={`LU-id för ${r.name || r.email}`}
                      value={r.lu_id}
                      onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, lu_id: e.target.value.trim().toLowerCase() } : x))}
                      placeholder={p === "needs_lu_id" ? "Behövs" : ""}
                      style={{ ...css.input, padding: "5px 8px", fontSize: 12, fontFamily: "'DM Mono', monospace", width: 120, marginBottom: 0,
                        borderColor: p === "needs_lu_id" ? G.gold : G.border }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <FL>Roll för alla</FL>
      <select style={css.input} value={roleData.role || ""} onChange={e => setRoleData({ role: e.target.value, program_id: "", course_id: "" })}>
        <option value="">Välj roll…</option>
        {Object.entries(assignableRoles).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {renderScope(roleData, setRoleData)}

      {error && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: G.red }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button onClick={submit} disabled={busy || !roleReady || blocking > 0}
          style={{ ...btn(true), opacity: busy || !roleReady || blocking > 0 ? 0.5 : 1 }}>
          {busy ? "Skickar…" : `Lägg till och mejla ${rows.length}`}
        </button>
        <button onClick={() => { setRows(null); setError(null); }} style={btn(false)}>Tillbaka</button>
        <button onClick={onClose} style={btn(false)}>Avbryt</button>
      </div>
      {blocking > 0 && <div style={{ marginTop: 8, fontSize: 12, color: G.red }}>Rätta raderna markerade i rött först.</div>}
    </>
  );
}

export { BulkAddUsers };
